import { Vector2 } from "three";
import { Polygon } from "./types";
import { cross, isPolygonClockwise, lineInfiniteIntersection } from "oc-threejs/Maths2";
import { v2 } from "oc-threejs/short3";

export const EPS = 1e-9;

// Tupeltypen für polygon-clipping
export type Pair = [number, number];
export type Ring = Pair[];
export type PolyRings = Ring[];
export type MultiPoly = PolyRings[];

export function vecToPairRing(poly: Polygon): Ring {
  return poly.map((p) => [p.x, p.y] as Pair);
}
export function pairRingToVec(ring: Ring): Polygon {
  return ring.map((pt) => v2(pt[0], pt[1]));
}

// Snapping: runde Koordinaten auf Raster, um winzige Gaps zu schließen
export function snapPair(p: Pair, grid = 1e-6): Pair {
  const rx = Math.round(p[0] / grid) * grid;
  const ry = Math.round(p[1] / grid) * grid;
  return [rx, ry];
}
export function snapRing(ring: Ring, grid = 1e-6): Ring {
  return ring.map((p) => snapPair(p, grid));
}
export function snapPoly(poly: PolyRings, grid = 1e-6): PolyRings {
  return poly.map((ring) => snapRing(ring, grid));
}
export function snapMultiPoly(mp: MultiPoly, grid = 1e-6): MultiPoly {
  return mp.map((poly) => snapPoly(poly, grid));
}

export function lineIntersection(
  p1: Vector2,
  d1: Vector2,
  p2: Vector2,
  d2: Vector2
): Vector2 | null {
  const p1b = v2(p1.x + d1.x, p1.y + d1.y);
  const p2b = v2(p2.x + d2.x, p2.y + d2.y);
  return lineInfiniteIntersection([p1, p1b], [p2, p2b], EPS);
}

export function samePoint(a: Vector2, b: Vector2, tol = 1e-6) {
  return a.distanceTo(b) <= tol;
}

// Hilfsfunktion: normalisiere Polygon (einzigartige Punkte, CCW-Sortierung), ohne auf 4 Punkte zu beschränken
export function normalizePolygon(pts: Polygon): Polygon {
  if (!pts || pts.length === 0) return [];
  // entferne nahezu identische Punkte
  const unique: Polygon = [];
  for (const p of pts) {
    if (!unique.some((q) => p.distanceTo(q) < 1e-9)) unique.push(v2(p));
  }
  if (unique.length < 3) return [];
  // Schwerpunkt
  const c = unique.reduce((acc, p) => acc.add(v2(p)), v2(0, 0)).multiplyScalar(1 / unique.length);
  unique.sort((p, q) => Math.atan2(p.y - c.y, p.x - c.x) - Math.atan2(q.y - c.y, q.x - c.x));
  // sicherstellen CCW
  if (isPolygonClockwise(unique)) unique.reverse();
  return unique;
}

// Helfer: Clipping eines (evtl. konvexen) Polygons gegen eine Halbebene, definiert durch Linie a->b.
// keepSideSign = 1 -> behalte die Seite, auf der cross(dir, p-a) >= 0 ist; -1 -> entgegengesetzt
export function clipPolygonByHalfplane(
  polygon: Polygon,
  a: Vector2,
  b: Vector2,
  keepSideSign: 1 | -1,
  eps = 1e-9
): Polygon {
  if (!polygon || polygon.length === 0) return [];
  const dir = v2().subVectors(b, a);
  const out: Polygon = [];
  for (let i = 0; i < polygon.length; i++) {
    const p = polygon[i];
    const q = polygon[(i + 1) % polygon.length];
    const cp = cross(dir.x, dir.y, p.x - a.x, p.y - a.y) * keepSideSign;
    const cq = cross(dir.x, dir.y, q.x - a.x, q.y - a.y) * keepSideSign;
    const pIn = cp >= -eps;
    const qIn = cq >= -eps;
    if (pIn) out.push(p);
    if (pIn !== qIn) {
      // Schnittpunkt zwischen Segment pq und Linie ab
      const ip = lineIntersection(p, v2().subVectors(q, p), a, dir);
      if (ip) out.push(ip);
    }
  }
  // normalisiere das Ergebnis (ohne Längenbegrenzung)
  return normalizePolygon(out);
}
